import React from 'react';
import cn from 'classnames';

import styled from '@benzinga/themetron';
import { formatEST, formatAfterHours, formatNumericDateTime } from '@benzinga/date-utils';
import { formatPrice, formatPriceWithCurrency, formatRoundFixed } from '@benzinga/utils';
import {
  QuoteSessionType,
  REGULAR,
  NO_TRADING,
  PRE_MARKET,
  AFTER_MARKET,
  DelayedQuote,
} from '@benzinga/quotes-manager';
import { Up } from '@benzinga/themed-icons';
import i18n from '@benzinga/translate';

export const UpArrow = <Up className="fill-current mr-1" height="0.8em" viewBox="0 0 24 24" width="0.8em" />;

export const DownArrow = (
  <Up className="rotate-180 fill-current mr-1" height="0.8em" viewBox="0 0 24 24" width="0.8em" />
);

export const isExtendedHours = (type: QuoteSessionType) =>
  type === PRE_MARKET || type === AFTER_MARKET || type === NO_TRADING;

interface TimeLabels {
  timestampClose: string;
  timestampEth: string;
}

interface ExtendedPriceData {
  change: number;
  changePercent: number;
  price: number;
  time: number;
  volume: number;
}

export interface QuotePriceProps {
  quote: DelayedQuote;
  sessionType?: QuoteSessionType;
  lastUpdateClassName?: string;
  showDelayedText?: boolean;
}

const calculateChange = (n1: number, n2: number): [number, number, number] => {
  const rawChange = n1 - n2;
  const change = parseFloat(rawChange.toFixed(2));
  const changePercent = n2 !== 0 ? parseFloat(((change / n2) * 100).toFixed(2)) : 0;
  return [n1, change, changePercent];
};

export const regularSessionQuote = (close?: number | null, previousClose?: number | null, price?: number | null) => {
  if (close && close > 0.0 && previousClose && previousClose > 0.0) {
    return calculateChange(close as number, previousClose as number);
  } else if (price && price > 0.0 && previousClose && previousClose > 0.0) {
    return calculateChange(price as number, previousClose as number);
  } else {
    return [undefined, undefined, undefined];
  }
};

export const extendedPriceData = (quote: DelayedQuote): ExtendedPriceData | null => {
  if (quote.close !== undefined) {
    const ethChange = quote.lastTradePrice - quote.close;
    const ethChangePercent = (ethChange / quote.close) * 100;
    return {
      change: ethChange,
      changePercent: ethChangePercent,
      price: quote.close,
      time: quote.ethTime,
      volume: quote.ethVolume,
    };
  }
  return null;
};

export const getTimeLabels = (
  quote: DelayedQuote,
  sessionType?: QuoteSessionType,
  options?: { longDate?: boolean; isRealTime?: boolean },
): TimeLabels => {
  const formatter = options?.isRealTime ? formatNumericDateTime : formatEST;
  const isExtended = sessionType ? isExtendedHours(sessionType) : false;
  let timestampClose =
    typeof quote.lastTradeTime === 'number'
      ? `${i18n.t('Quote.Price.last-update', { ns: 'quote' })}: ${formatter(quote.lastTradeTime, options?.longDate)} `
      : '';
  let timestampEth = '';
  if (isExtended && quote.lastTradeTime) {
    if (sessionType === PRE_MARKET) {
      const previousCloseDate = quote.previousCloseDate ? new Date(quote.previousCloseDate).getTime() : null;
      timestampClose = previousCloseDate
        ? `${i18n.t('Quote.Price.at-close', { ns: 'quote' })}: ${formatEST(previousCloseDate, true)} EST`
        : '';
    } else {
      const closeDate = quote.closeDate ? new Date(quote.closeDate).getTime() : null;
      timestampClose = closeDate
        ? `${i18n.t('Quote.Price.at-close', { ns: 'quote' })}: ${formatAfterHours(closeDate, options?.longDate)} EST`
        : '';
    }
    if (quote.ethPrice !== undefined) {
      let timestampEthPrefix = i18n.t('Quote.Market.pre-market', { ns: 'quote' });
      if (sessionType === AFTER_MARKET || sessionType === NO_TRADING) {
        timestampEthPrefix = i18n.t('Quote.Market.after-hours', { ns: 'quote' });
      }
      if (sessionType === PRE_MARKET) {
        timestampEth =
          typeof quote.askTime === 'number'
            ? `${timestampEthPrefix}: ${formatEST(quote.askTime, options?.longDate)} EST`
            : '';
      } else {
        timestampEth =
          typeof quote.ethTime === 'number'
            ? `${timestampEthPrefix}: ${formatEST(quote.ethTime, options?.longDate)} EST`
            : '';
      }
    }
  }
  return {
    timestampClose,
    timestampEth,
  };
};

export const getArrow = (change: number) => {
  if (change < 0) {
    return DownArrow;
  } else if (change > 0) {
    return UpArrow;
  }
  return null;
};

// Remove showDelayedProp when we want to reenable
export const QuotePriceFull = ({
  lastUpdateClassName,
  quote,
  sessionType,
  showDelayed = false,
  showDelayedText = true,
}: QuotePriceProps & { showDelayed?: boolean }) => {
  // const showDelayed = false; //sessionType !== NO_TRADING;
  return (
    <div className="quote-price-full-wrapper">
      <QuotePriceFullContainer className="quote-price-full-container">
        <QuotePrice
          lastUpdateClassName={lastUpdateClassName}
          quote={quote}
          sessionType={sessionType}
          showDelayedText={showDelayedText}
        />
        <div className="flex flex-col secondary-quote-container">
          {sessionType !== REGULAR && (
            <QuotePriceExtended
              lastUpdateClassName={lastUpdateClassName}
              quote={quote}
              sessionType={sessionType}
              showDelayedText={showDelayedText}
            />
          )}
        </div>
      </QuotePriceFullContainer>
      {showDelayed && (
        <div className="whitespace-nowrap text-xs">
          <i>15 minutes delayed</i>
        </div>
      )}
    </div>
  );
};

const QuotePriceFullContainer = styled.div`
  display: flex;
  align-items: baseline;
  flex-direction: column;
  gap: 8px;
`;

export const QuotePrice = ({ lastUpdateClassName, quote, sessionType, showDelayedText }: QuotePriceProps) => {
  const labels = getTimeLabels(quote, sessionType);
  const isCrypto = quote?.type === 'CRYPTO';

  const closeChange =
    typeof quote?.close === 'number' && typeof quote?.previousClosePrice === 'number'
      ? quote?.close - quote?.previousClosePrice
      : null;

  const closeChangePercent =
    closeChange && quote?.previousClosePrice ? (closeChange / quote.previousClosePrice) * 100 : null;

  return (
    <QuotePriceContainer
      absoluteChange={isCrypto ? quote.change : closeChange}
      className="primary"
      lastUpdateClassName={lastUpdateClassName}
      percentageChange={isCrypto ? quote.changePercent : closeChangePercent}
      price={isCrypto ? quote.lastTradePrice : quote.close}
      quote={quote}
      sessionType={sessionType}
      showDelayedText={showDelayedText}
      timeLabel={labels.timestampClose}
    />
  );
};

export const QuotePriceExtended = ({ lastUpdateClassName, quote, sessionType, showDelayedText }: QuotePriceProps) => {
  const labels = getTimeLabels(quote, sessionType);
  const extendedData = extendedPriceData(quote);
  if (!extendedData) return null;
  return (
    <QuotePriceContainer
      absoluteChange={extendedData.change}
      lastUpdateClassName={lastUpdateClassName}
      percentageChange={extendedData.changePercent}
      price={extendedData.price}
      quote={quote}
      sessionType={sessionType}
      showDelayedText={showDelayedText}
      timeLabel={labels.timestampEth}
    />
  );
};

export const QuoteChange: React.FC<{
  absoluteChange?: number | null;
  positiveColor?: string;
  negativeColor?: string;
}> = ({ absoluteChange, negativeColor = 'text-red-500', positiveColor = 'text-green-500' }) => {
  if (!absoluteChange) return null;

  return (
    <span
      className={cn('quote-change font-semibold', {
        [`${positiveColor}`]: absoluteChange > 0,
        [`${negativeColor}`]: absoluteChange < 0,
      })}
    >
      {formatPrice(absoluteChange, {
        fixedPlace: Math.abs(absoluteChange) < 0.1 ? undefined : 2,
        removeTrailingZero: true,
        showNearZero: Math.abs(absoluteChange) < 0.000001,
      })}
    </span>
  );
};

export const QuoteChangePercent: React.FC<{
  percentageChange: number;
  showBrackets?: boolean;
  positiveColor?: string;
  negativeColor?: string;
}> = ({ negativeColor = 'text-red-500', percentageChange, positiveColor = 'text-green-500', showBrackets = false }) => {
  const formatedPercent = formatRoundFixed(percentageChange, 2);
  if (!percentageChange) {
    return <span className="text-black text-base font-semibold">N/A</span>;
  }
  return (
    <span
      className={cn('quote-change-percent font-semibold', {
        [`${positiveColor}`]: percentageChange > 0,
        [`${negativeColor}`]: percentageChange < 0,
      })}
    >
      {showBrackets ? `[${formatedPercent}%]` : `${formatedPercent}%`}
    </span>
  );
};

export interface QuotePriceContainerProps {
  absoluteChange: number | null;
  className?: string;
  currency?: string;
  percentageChange: number | null;
  price: number;
  sessionType?: QuoteSessionType;
  timeLabel: string;
  quote?: DelayedQuote;
  lastUpdateClassName?: string;
  showDelayedText?: boolean;
}

export const QuotePriceContainer = ({
  absoluteChange,
  className,
  currency,
  lastUpdateClassName,
  percentageChange,
  price,
  quote,
  sessionType,
  showDelayedText = true,
  timeLabel,
}: // time,
QuotePriceContainerProps) => {
  const arrow = getArrow(absoluteChange ?? 0);
  const isExtended = sessionType ? isExtendedHours(sessionType) : false;
  return (
    <QuotePriceWrapper className={className}>
      <div className="quote-price-data">
        {typeof price === 'number' && <div className="quote-price">{formatPriceWithCurrency(price, currency)}</div>}
        <div className="quote-change-data">
          {arrow}
          {typeof absoluteChange === 'number' && (
            <span className="mr-1">
              <QuoteChange absoluteChange={absoluteChange} />
            </span>
          )}
          {typeof percentageChange === 'number' && (
            <QuoteChangePercent percentageChange={percentageChange} showBrackets={true} />
          )}
        </div>
      </div>
      {timeLabel && (
        <div className={`last-update-wrapper text-bzgray-600 text-xs ${lastUpdateClassName ?? ''}`}>
          <span className={`timelabel`}>{timeLabel}</span>
          {!isExtended && quote?.lastTradeTime && (
            <span>
              {showDelayedText && (
                <>
                  {' '}
                  (Delayed 15-Minutes)
                  <br />
                </>
              )}
              <a
                data-action="Quote Page Get Real Time with PRO"
                href="https://pro.benzinga.com/?utm_source=quotes-page"
                rel="noreferrer"
                target="_blank"
              >
                Get Real Time Here
              </a>
            </span>
          )}
        </div>
      )}
    </QuotePriceWrapper>
  );
};

const QuotePriceWrapper = styled.div`
  .quote-price-data,
  .quote-change-data {
    align-items: baseline;
    display: flex;
  }
  .quote-price-data {
    flex-wrap: wrap;
    flex-direction: column;
  }
  .quote-price {
    font-size: 1.2rem;
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
    margin-right: 0.4rem;
    color: #000000;
  }
  &.primary {
    .quote-price {
      font-size: 1.8rem;
    }
    .quote-change,
    .quote-change-percent {
      font-size: 1.3rem;
      line-height: 1.3rem;
    }
  }
  @media screen and (max-width: 800px) {
    .quote-price-data {
      margin-bottom: 0.15rem;
      flex-direction: unset;
    }
  }
`;
